/**
 * External Exports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { parse, format } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuButton } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Exports
 */
import { EmployeeSharedWithProps } from './FeedbackModal';
import { BlueChip } from '../../shared/chips/blueChip';

import { logTaskClick } from '../../../utils/analyticsHelpers';
import { reverseName, toTitleCase } from '../../../utils/helpers';

/**
 * Gloabl Type Definition Imports
 */
import { Feedback, Unit } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  CompletedTextDiv,
  DateDiv,
  FeedbackViewDiv,
  FeedbackViewDiv2Column,
  FeedbackViewSubDiv,
  LabelContent,
  LabelContentBold,
  LabelContentPatient,
  LabelHeading,
  ModalCardContent,
  ModalFooter,
  TitleSpanView
} from './FeedbackModal.styles';

interface IFeedbackModalViewProps {
  associatedPatient: {
    facilityId?: string;
    accountNum?: string;
  };
  employeeSharedWith: EmployeeSharedWithProps[] | undefined;
  feedbackInfo: Feedback;
  units: Unit[];
  userSection: string;
  handleRedirect: () => void;
  onClose: () => void;
}
const FeedbackModalView: FC<IFeedbackModalViewProps> = ({
  associatedPatient,
  employeeSharedWith,
  feedbackInfo,
  units,
  userSection,
  handleRedirect,
  onClose
}) => {
  const {
    bed,
    category,
    completed,
    completedDate,
    department,
    description,
    employeeFirstName,
    employeeFullName,
    employeeLastName,
    employeeId,
    patientFirstName,
    patientLastName,
    room,
    roundId,
    subCategory,
    taskCategory,
    taskSubtype,
    unitId,
    userFullName
  } = feedbackInfo;
  const [completedDateFormatted, setCompletedDateFormatted] = useState('');

  const { logTrackingEvent } = useAnalyticsApi();

  const handleClose = () => {
    logTrackingEvent(logTaskClick('feedback', 'close'));
    onClose();
  };

  const unitName = useMemo(() => {
    const foundUnit: Unit[] = units.filter(
      (unit: Unit) => unit.unitId === unitId
    );
    return foundUnit[0]?.unit;
  }, [units]);

  useEffect(() => {
    if (completedDate) {
      const newdate = parse(completedDate, 'yyyyMMddHHmmss', new Date());
      const formatted = format(newdate, 'MM-dd-yyyy @ hh:mm aa');
      setCompletedDateFormatted(formatted);
    }
  }, [completedDate]);

  return (
    <>
      <CardHeader unit="header">
        <div>
          <TitleSpanView>{`${toTitleCase(userSection)} | ${toTitleCase(
            taskSubtype
          )}`}</TitleSpanView>
        </div>
        <NeuButton
          id="Feedback-Modal-Close-Button"
          aria-label="close panel"
          kind="tertiary"
          icon="close"
          size="small"
          onClick={handleClose}
        >
          Close
        </NeuButton>
      </CardHeader>
      <ModalCardContent unit="content">
        <FeedbackViewDiv2Column>
          {userSection === 'patient' ? (
            <FeedbackViewSubDiv style={{ flexBasis: '61%' }}>
              <LabelHeading>Patient</LabelHeading>
              <LabelContentPatient
                color={
                  associatedPatient.facilityId &&
                  associatedPatient.accountNum &&
                  roundId
                    ? '#206DA3'
                    : '#191919'
                }
                cursorType={
                  !!(
                    associatedPatient.facilityId &&
                    associatedPatient.accountNum &&
                    roundId
                  )
                }
                onClick={
                  associatedPatient.facilityId &&
                  associatedPatient.accountNum &&
                  roundId
                    ? () => handleRedirect()
                    : undefined
                }
              >
                {!patientLastName || !patientFirstName
                  ? 'Unknown'
                  : toTitleCase(reverseName(patientFirstName, patientLastName))}
              </LabelContentPatient>
            </FeedbackViewSubDiv>
          ) : (
            <div>
              <LabelHeading>Employee</LabelHeading>
              <LabelContentPatient
                // TODO: Why is blue link turned off on emp feedback
                // onClick={() => handleRedirect()}
                // cursorType={!!(roundId && employeeId)}
                // color={roundId && employeeId ? '#206DA3' : '#191919'}
                className="neu-text-blue-60"
              >
                {`${
                  employeeLastName ? `${toTitleCase(employeeLastName)}, ` : ''
                }
                  ${employeeFirstName ? toTitleCase(employeeFirstName) : ''}`}
                {`${!employeeLastName && !employeeFirstName ? 'Unknown' : ''}`}
                {`${employeeId ? ` | ${employeeId.toUpperCase()}` : ''}`}
              </LabelContentPatient>
            </div>
          )}
          {userSection === 'patient' && (
            <FeedbackViewSubDiv>
              <LabelHeading>Room</LabelHeading>
              <LabelContentBold>
                {room ? `${room}${bed || ''}` : 'N/A'}
              </LabelContentBold>
            </FeedbackViewSubDiv>
          )}
        </FeedbackViewDiv2Column>
        {userSection === 'patient' ? (
          <FeedbackViewDiv>
            <LabelHeading>Unit</LabelHeading>
            <LabelContentBold>{`${
              unitId ? unitName : 'Unknown'
            }`}</LabelContentBold>
          </FeedbackViewDiv>
        ) : (
          <FeedbackViewDiv>
            <LabelHeading>Department</LabelHeading>
            <LabelContentBold>{department || 'Unknown'}</LabelContentBold>
          </FeedbackViewDiv>
        )}
        {userSection === 'patient' && (
          <FeedbackViewDiv>
            <LabelHeading>Employee</LabelHeading>
            <LabelContent>
              <BlueChip
                name={`${employeeFullName} ${
                  employeeId ? employeeId.toUpperCase() : ''
                }`}
                removable={false}
              />
            </LabelContent>
          </FeedbackViewDiv>
        )}
        <FeedbackViewDiv>
          <LabelHeading>Creator</LabelHeading>
          <LabelContentBold className="my-1">{userFullName}</LabelContentBold>
        </FeedbackViewDiv>
        {!category ? (
          <FeedbackViewDiv>
            <LabelHeading>Feedback Category</LabelHeading>
            <LabelContentBold>{taskCategory.join(', ')}</LabelContentBold>
          </FeedbackViewDiv>
        ) : (
          <>
            <FeedbackViewDiv>
              <LabelHeading>Feedback Category & Subcategory</LabelHeading>
              <LabelContentBold>
                {category}
                {subCategory}
              </LabelContentBold>
            </FeedbackViewDiv>{' '}
          </>
        )}
        {/* TODO: Add subcategory */}
        <FeedbackViewDiv>
          <LabelHeading>Details</LabelHeading>
          <LabelContentBold>{description || 'Unknown'}</LabelContentBold>
        </FeedbackViewDiv>
        <FeedbackViewDiv>
          <LabelHeading>Share With</LabelHeading>
          <LabelContentBold>
            {employeeSharedWith && employeeSharedWith.length > 0
              ? employeeSharedWith.map((item: EmployeeSharedWithProps) => {
                  return (
                    <BlueChip
                      name={`${item.fullName} ${item.employeeId}`}
                      removable={false}
                    />
                  );
                })
              : 'N/A'}
          </LabelContentBold>
        </FeedbackViewDiv>
      </ModalCardContent>
      <ModalFooter unit="footer">
        {completed && (
          <>
            <NeuIcon color="green" size="3x">
              check_circle
            </NeuIcon>
            <LabelContent>
              <CompletedTextDiv>Completed</CompletedTextDiv>
              <DateDiv>{completedDateFormatted}</DateDiv>
            </LabelContent>
          </>
        )}
      </ModalFooter>
    </>
  );
};

export default FeedbackModalView;
